import React, { useState, useEffect } from 'react';
import './ContactUs.css';

import { ContactInfo, Heading, PageTitle } from '../../components';

import { FaUser, FaEnvelope, FaPhone, FaComment } from 'react-icons/fa';

const CONTACT_EMAIL = 'the23coffeeshop@yahoo.com';

// Regular expressions for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const Contact = () => {

  // State to manage form input values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    message: ''
  });
  
  // State for handling success and error messages
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailLink, setEmailLink] = useState('');

  // Effect to clear success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const buildContactEmail = () => {
    const subject = `Contact Form Message from ${formData.name}`;
    const body = [
      'Contact Form Message',
      '',
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.number}`,
      '',
      'Message:',
      formData.message,
    ].join('\n');

    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openEmailDraft = (href) => {
    const emailWindow = window.open(href, '_blank', 'noopener,noreferrer');

    if (emailWindow) {
      return;
    }

    const emailAnchor = document.createElement('a');
    emailAnchor.href = href;
    emailAnchor.target = '_blank';
    emailAnchor.rel = 'noopener noreferrer';
    emailAnchor.style.display = 'none';
    document.body.appendChild(emailAnchor);
    emailAnchor.click();
    document.body.removeChild(emailAnchor);
  };

  // Function to handle form submission
  const handleSubmitForm = (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setIsSubmitting(true);
    setError('');

    try {

      // Client-side validation
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.message.trim()) validationErrors.push('Message is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | ')); // Concatenate errors with '|'
      }

      const contactEmailLink = buildContactEmail();
      setEmailLink(contactEmailLink);
      openEmailDraft(contactEmailLink);

      setMailSent(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle input changes and update state
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
 

  return (
    <> 
      <PageTitle title={'contact us'} page={'contact'} />
      <section className='contact'>

        <div className='box-container'> 

          {/* Contact Form */}
          <form className='dark-form' onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Send Message"} linear sideHeading />

            {/* Name Input Field */}
            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  className='box'
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                  disabled={isSubmitting}
                  required
                />
              </div>
            </div>

            {/* Email Input Field */}
            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  className='box'
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            {/* Phone Number Input Field */}
            <div className='input-box'>
              <div className='input-field'>
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="text"
                  className='box'
                  id="number"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  disabled={isSubmitting}
                  required 
                />
              </div>
            </div>

            {/* Message Input Field */}
            <div className='input-box'>
              <div className='input-field text-area'>
                <label htmlFor="message"><FaComment /></label>
                <textarea
                  cols="30"
                  rows="10"
                  id="message"
                  className='box'
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Message"
                  disabled={isSubmitting}
                  required 
                ></textarea>
              </div>
            </div>

            {/* Submit Button */}
            <button type="submit" className='btn' disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && <div className="alert error">{error.split(' | ').map((msg, i) => (<span key={i}>{msg}</span>))}</div>}
            {mailSent && (
              <div className="alert success">
                <span>Email draft opened with your message. If it did not open, use the link below.</span>
                {emailLink && (
                  <a href={emailLink} className="btn" target="_blank" rel="noopener noreferrer">
                    Open email draft
                  </a>
                )}
              </div>
            )}
          </form>

          {/* Embedded Google Map */}
          <iframe 
            src="https://www.google.com/maps?q=2667%20Powder%20Springs%20Rd%20SW%20Ste%20105%2C%20Marietta%2C%20GA%2030064&output=embed" 
            allowFullScreen 
            loading="lazy"
            title="Google Maps Location"
          ></iframe>

        </div>

        {/* Contact Info Component */}
        <ContactInfo />

     </section>
     </>
   )

}
 
 export default Contact
